import { NotificationItemMap, SiteWideNotification } from '../../pages/notification/notification-models';
import { DocumentUploadSetting } from './document-upload-setting';
import { ApiErrorMessageMap } from './error-model';

export interface TcsmContentPropsState<TProps> {
	contentLoaded: boolean;
	epiContent: TProps;
}

export interface TcsmSiteSettingsState {
	siteSettingsLoaded: boolean;
	siteSettings: TcsmSiteSettingsProps;
}

export enum TcsmEpiApiActionTypes {
	SET_EPI_PROPS = 'SET_EPI_PROPS',
	SET_SITE_SETTINGS_PROPS = 'SET_SITE_SETTINGS_PROPS',
	SET_LABEL_EPI_PROPS = 'SET_LABEL_EPI_PROPS',
	SET_PERSONALINFO_PROPS = 'SET_PERSONALINFO_PROPS',
	SET_RELATIONSHIPS_PROPS = 'SET_RELATIONSHIPS_PROPS',
	SET_COMPENSATION_PROPS = 'SET_COMPENSATION_PROPS',
	SET_EMPLOYMENT_PROPS = 'SET_EMPLOYMENT_PROPS',
	SET_INSTITUTION_PROPS = 'SET_INSTITUTION_PROPS',
	SET_SEARCH_PROPS = 'SET_SEARCH_PROPS',
	SET_PERSON_SEARCH_PROPS = 'SET_PERSON_SEARCH_PROPS',
	SET_GROUPS_BILLING_PROPS = 'SET_GROUPS_BILLING_PROPS',
	SET_ANNUAL_ENROLLMENT_PROPS = 'SET_ANNUAL_ENROLLMENT_PROPS',
	SET_PENSION_ENROLLMENT_PROPS = 'SET_PENSION_ENROLLMENT_PROPS',
	SET_NEW_HIRE_PROPS = 'SET_NEW_HIRE_PROPS',
	SET_MID_YEAR_PROPS = 'SET_MID_YEAR_PROPS',
	SET_TERMINATION_PROPS = 'SET_TERMINATION_PROPS',
	SET_GPS_PROPS = 'SET_GPS_PROPS',
	SET_MAP_PORTAL_PROPS = 'SET_MAP_PORTAL_PROPS',
	SET_EPI_PAGE_DATA = 'SET_EPI_PAGE_DATA',
	UNLOAD_EPI_PAGE_DATA = 'UNLOAD_EPI_PAGE_DATA',
	SET_PODS_SETTINGS = 'SET_PODS_SETTINGS',
	SET_COMP_REPORT_SETTINGS = 'SET_COMP_REPORT_SETTINGS',
	SET_CLERGY_BIO_SETTINGS = 'SET_CLERGY_BIO_SETTINGS',
	SET_PARTICIPANT_ENROLLMENT_SETTINGS = 'SET_PARTICIPANT_ENROLLMENT_SETTINGS',
	SET_BENEFICIARY_SETTINGS = 'SET_BENEFICIARY_SETTINGS',
	SET_MYCPGMIDYEAR_SETTINGS = 'SET_MYMIDYEAR_SETTINGS',
	SET_CIC_SETTINGS = 'SET_CIC_SETTINGS',

}

export interface TcsmEpiProps {
	pageTitle: string;
	apiResponseMessages?: ApiErrorMessageMap[];
}

export interface LabelSettingProps extends TcsmEpiProps {
	addLabel: string,
	editLabel: string,
	salutationLabel: string,
	firstNameLabel: string,
	middleNameLabel: string,
	lastNameLabel: string,
	suffixLabel: string,
	preferredLabel: string,
	genderLabel: string,
	genderIdentityLabel: string,
	genderIdentityDescription: string,
	genderIdentitySelfDescribeLabel: string,
	dobLabel: string,
	plusSixtyFiveAlertTitle: string,
	plusSixtyFiveAlertDescription: string,
	ssnTinLabel: string,
	mailingAddressTitle: string,
	countryLabel: string,
	addressLabel: string,
	addressLabel1: string,
	addressLabel2: string,
	addressLabel3: string,
	cityLabel: string,
	stateLabel: string,
	provinceLabel: string,
	postalLabel: string,
	phoneSectionTitle: string,
	phoneSectionDescription: string,
	businessPhoneLabel: string,
	cellPhoneLabel: string,
	homePhoneLabel: string,
	extensionLabel: string,
	emailSectionTitle: string,
	emailSectionDescription: string,
	businessEmailLabel: string,
	personalEmailLabel: string,
	maritalStatusTitle: string,
	maritalStatusLabel: string,
	currentSpouseLabel: string,
	tooltipMessage: string,
	genderTooltipMessage: string,
	submitText: string,
	continueText: string,
	cancelButtonText: string,
	cancelText: string,
	cancelGoBackText: string,
	goBackText: string,
	currentPublishedLabel: string,
	newMailingLabel: string,
	requiredFormat: string,
	atLeastOneEmail: string,
	atLeastOnePhone: string,
	notNegative: string;
	numberPositive: string;
	emailFormatError: string,
	yesText: string,
	noText: string,
	institutionName: string,
	institutionNameTooltip: string,
	institutionLegalName: string,
	institutionLegalNameTooltip: string,
	institutionProvince: string,
	mailingAddress: string,
	physicalAddress: string,
	institutionPhysicalAddressTooltip: string,
	contactInformation: string,
	officePhone: string,
	faxPhone: string,
	institutionEmailAddress: string,
	website: string,
	presentDateLabel: string,
	numberFourDigits: string,
	addressExpiredError: string,
	endDateBeforeStartDateError: string,
	dateFutureError: string,
	specialCharactersError: string,
	maxCharactersError: string,
	invalidError: string,
	singleText: string,
	marriedText: string,
	divorcedText: string,
	widowedText: string,
	ssnOrDobRequired: string,
	ssnInvalid: string;
	naLabel: string;
	// general
	clientNumberLabel: string;
	selectDefaultText: string;
	yearsText: string,
	loadingText: string,
	selectMatchPersonRequired: string,
	myDocumentsTitle: string,
	myDocumentsDescription: string,
	myDocumentsEmptyListMessage: string,
	myDocumentsDownloadError: string,
	dateUploadedLabel: string,
	documentLinkLabel: string,
	businessEmailRequired: string,
	endDateAfterEndDateError?: string,
	min2CharacterErrorMessage?: string,
	mustContainAlphabeticalErrorMessage?: string,

}

export interface RouteBreadcrumb {
	route: string,
	breadcrumb: string,
}

export interface SuccessMessageWithNBA {
	title: string,
	message: string,
}

export interface LockedFeatureReason {
	lockedReasonKey: string;
	lockedReasonSummary: string;
}

export interface LockedFeature {
	locksKey: string;
	lockedReasonMessages: LockedFeatureReason[];
	generalMessage: string;
}

export interface TcsmSiteSettingsProps {
	signInPage: string;
	error400: string;
	error401Title: string;
	error401Message: string;
	error403Title: string;
	error403Message: string;
	error401403HomeLinkText: string;
	error404Title: string;
	error404: string;
	error500: string;
	errorTimeout: string;
	defaultError: string;
	apiResponseMessages: { [key: string]: SuccessMessageWithNBA }
	apiResponseErrorMessages: { [key: string]: string }
	routeBreadcrumbs: RouteBreadcrumb[];
	externalLinkModalMessage: string;
	remoteSupportLinkModalMessage: string;
	externalLinkModalCancelButtonText: string;
	externalLinkModalContinueButtonText: string;
	docUploadChipTitle?: string;
	docUploadSectionTitle?: string;
	docUploadSectionDescription?: string;
	documentUploadSettings?: DocumentUploadSetting[];
	docUploadToastMessage?: string;
	docUploadAnchorText?: string;
	docUploadToastInternationalMessage?: string;
	docUploadToastDemographicInternationalMessage?: string;
	genericDocUploadText?: string;
	genericDocUploadPageTitle?: string;
	secondaryNavLabel?: string;
	//Alert and Notifications
	alertNotificationCtaText?: string;
	alertNotificationPageTitle?: string;
	alertNotificationNoNotificationMessage?: string;
	siteWideAlerts?: SiteWideNotification[];
	notificationMapping?: NotificationItemMap[];
	lockedPagesMessaging: LockedFeature[];
	searchIndexUrl: string;
	adminQuickNavigationMenu: TCSMQuickNavigationLink[];
	groupsQuickNavigationMenu: TCSMQuickNavigationLink[];
	institutionQuickNavigationMenu: TCSMQuickNavigationLink[];
	clergyBioQuickNavigationMenu: TCSMQuickNavigationLink[];


	//site maintenance

	myCpgMaintenanceOn?: boolean;
	myCpgMaintenanceStartTime?: string;
	myCpgMaintenanceEndTime?: string;
	myCpgSiteMaintenanceText?: string;
	mapMaintenanceOn?: boolean;
	mapMaintenanceStartTime?: string;
	mapMaintenanceEndTime?: string;
	mapSiteMaintenanceText?: string;

	greetingText?: string;

	docuSignAccountId?: string;
	docuSignIss?: string;
	docuSignJSUrl?: string;
	docuSignAppBaseUri?: string;
	docuSignAppFrameAncestorUri?: string;
	docuSignConfigurationErrorMessage?: string;
	docuSignEnvelopeErrorMessage?: string;
}

export const DefaultTcsmSiteSettingsProps: TcsmSiteSettingsProps = {
	signInPage: '',
	error400: '',
	error401Message: '',
	error401Title: '',
	error403Title: '',
	error401403HomeLinkText: '',
	error403Message: '',
	error404Title: '',
	error404: '',
	error500: '',
	errorTimeout: '',
	defaultError: '',
	apiResponseMessages: {},
	apiResponseErrorMessages: {},
	routeBreadcrumbs: [],
	lockedPagesMessaging: [],
	externalLinkModalContinueButtonText: '',
	externalLinkModalCancelButtonText: '',
	externalLinkModalMessage: '',
	remoteSupportLinkModalMessage: '',
	searchIndexUrl: '',
	adminQuickNavigationMenu: [],
	groupsQuickNavigationMenu: [],
	institutionQuickNavigationMenu: [],
	clergyBioQuickNavigationMenu: [],
};

export const DefaultSiteSettingsState: TcsmSiteSettingsState = {
	siteSettingsLoaded: false,
	siteSettings: DefaultTcsmSiteSettingsProps,
};

export interface TCSMQuickNavigationLink {
	linkText: string,
	fallbackMatch?: string,
	linkUrl: string,
	deepLink: boolean,
	rolesMatch?: string,
	employeeContext?: string,
}
